import React from "react";
import { Link } from "react-router-dom";
import { MapPin, FileText, CalendarDays, AlertTriangle, Activity } from "lucide-react";
import StatusPill from "./StatusPill";
import { formatDate, formatRelative } from "../lib/constants";

function dueLabel(due) {
  if (!due) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const d = new Date(`${due}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const diffDays = Math.round((d - today) / 86400000);
  if (diffDays < 0) return { text: `Overdue · ${Math.abs(diffDays)}d`, overdue: true };
  if (diffDays === 0) return { text: "Due today", overdue: false };
  if (diffDays <= 14) return { text: `Due in ${diffDays}d`, overdue: false };
  return { text: `Due ${formatDate(due)}`, overdue: false };
}

export const ProjectCard = ({ project, to }) => {
  const due = dueLabel(project.due_date);
  return (
  <Link
    to={to}
    className="card-surface hover-lift p-6 block group fade-up"
    data-testid={`project-card-${project.id}`}
  >
    <div className="flex items-start justify-between gap-4 mb-6">
      <div className="min-w-0">
        <div className="label mb-1 text-[color:var(--atech-text-muted)]">
          {project.client_name}
        </div>
        <h3 className="font-display text-xl text-white tracking-tight leading-tight truncate group-hover:text-[color:var(--atech-copper)] transition-colors">
          {project.project_name}
        </h3>
      </div>
      <StatusPill status={project.current_status} />
    </div>

    {project.address && (
      <div className="flex items-center gap-2 text-xs text-[color:var(--atech-text-dim)] mb-3">
        <MapPin size={12} />
        <span className="truncate">{project.address}</span>
      </div>
    )}

    {(project.start_date || project.due_date) && (
      <div className="flex items-center gap-3 flex-wrap text-xs text-[color:var(--atech-text-dim)] mb-4">
        {project.start_date && (
          <span className="inline-flex items-center gap-1.5">
            <CalendarDays size={12} /> {formatDate(project.start_date)}
          </span>
        )}
        {due && (
          <span className={`inline-flex items-center gap-1.5 ${due.overdue ? "text-[#F87171]" : ""}`}>
            {due.overdue ? <AlertTriangle size={12} /> : <CalendarDays size={12} />}
            {due.text}
          </span>
        )}
      </div>
    )}

    <div className="flex items-center justify-between pt-5 border-t border-white/5">
      <div className="flex items-center gap-3 text-xs">
        <span className="font-mono text-[color:var(--atech-text-dim)]">{project.project_reference}</span>
        <span className="text-[color:var(--atech-text-muted)]">·</span>
        <span className="text-[color:var(--atech-text-muted)]">{project.project_type}</span>
      </div>
      <div className="flex items-center gap-1.5 text-xs text-[color:var(--atech-text-muted)]">
        <FileText size={12} />
        <span>{project.document_count || 0}</span>
      </div>
    </div>

    <div className="mt-3 flex items-center justify-between text-[10px] uppercase tracking-[0.16em] text-[color:var(--atech-text-muted)]">
      <span>Updated {formatDate(project.updated_at)}</span>
      {project.last_activity_at && (
        <span
          className="inline-flex items-center gap-1.5 normal-case tracking-normal text-[11px]"
          title={`Last activity ${formatDate(project.last_activity_at)}`}
          data-testid={`last-activity-${project.id}`}
        >
          <Activity size={10} />
          {formatRelative(project.last_activity_at)}
        </span>
      )}
    </div>
  </Link>
  );
};

export default ProjectCard;
